import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { DetailItem } from "./SalesTaxTrackerSlider";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { ConditionalRender } from "./SalesTaxTracker";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { Status } from "./Status";
import { statusMap } from "./SalesTaxTrackerTable";
import { RegistrationDetailsResponse } from "store/apis/salesTaxRegistration";
import {
  PreparerColumn,
  ServiceTeamColumn,
} from "components/tasks/taskForForeignCA/ForeignCATask";
import { SALES_TAX } from "constants/apps";

export const RegistrationAboutAccordion = ({
  data,
  groupId,
  entityId,
}: {
  data?: RegistrationDetailsResponse;
  groupId: string;
  entityId: string;
}) => {
  const taskData = {
    uuid: data?.uuid!,
    assigned_team: data?.service_team,
    assigned_to: {
      uuid: data?.preparer?.profile_id,
      name: data?.preparer?.name,
    },
  };

  return (
    <Accordion.Item
      value="ABOUT"
      className="t-border t-border-neutral-10 t-rounded-lg t-w-full"
    >
      <Accordion.Trigger>
        <p className="t-text-subtext t-text-text-100 t-m-0">About</p>
      </Accordion.Trigger>
      <Accordion.Content>
        <div className="t-grid t-grid-cols-2 t-justify-between t-gap-6">
          <DetailItem label="Task Type">
            <span className="t-text-subtext t-text-purple">
              Sales Tax Registration
            </span>
          </DetailItem>
          <DetailItem label="Task Status">
            <Status status={data?.status!}>
              {statusMap[data?.status! as keyof typeof statusMap]}
            </Status>
          </DetailItem>
          <DetailItem label="State">
            <span className="t-text-subtext t-text-text-60">{data?.state}</span>
          </DetailItem>
          <DetailItem label="Pricing">
            <span className="t-text-subtext t-text-text-60">
              <AmountSuperScript amount={75} />
            </span>
          </DetailItem>

          <DetailItem label="Service Team">
            <span className="t-text-subtext t-text-text-60 t-min-w-full">
              <ServiceTeamColumn tracker={SALES_TAX} taskData={taskData} />
            </span>
          </DetailItem>
          <DetailItem label="Preparer">
            <span className="t-text-subtext t-text-text-60 t-min-w-full">
              <PreparerColumn tracker={SALES_TAX} taskData={taskData} />
            </span>
          </DetailItem>

          <DetailItem
            label="Login Email ID"
            toolTipText={
              <span>
                Login email for accessing <br />
                Sidr account
              </span>
            }
          >
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                fallback="-"
                condition={Boolean(data?.login_email)}
              >
                {data?.login_email}
              </ConditionalRender>
            </span>
          </DetailItem>
          <DetailItem label="Assigned on">
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                fallback="-"
                condition={Boolean(data?.assigned_on)}
              >
                {dayjs(data?.assigned_on).format(DISPLAY_DATE)}
              </ConditionalRender>
            </span>
          </DetailItem>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

export const RegistrationDetails = ({
  data,
}: {
  data?: RegistrationDetailsResponse;
}) => {
  return (
    <Accordion.Item
      value="DETAILS"
      className="t-border t-border-neutral-10 t-rounded-lg t-w-full"
    >
      <Accordion.Trigger>
        <p className="t-text-subtext t-text-text-100 t-m-0">
          Registration details
        </p>
      </Accordion.Trigger>
      <Accordion.Content>
        <div className="t-flex t-flex-col t-gap-4">
          <DetailItem
            label="Filing service start date"
            toolTipText={
              <span>
                Date since we began filing for <br /> you (e.g., April 1 for Q2
                filing)
              </span>
            }
          >
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                condition={Boolean(data?.filing_start_date)}
                fallback="-"
              >
                {dayjs(data?.filing_start_date).format(DISPLAY_DATE)}
              </ConditionalRender>
            </span>
          </DetailItem>
          <DetailItem
            label="Existing Tax account ID in this state"
            toolTipText={
              <span>
                e.g. Franchise Tax ID, Withholding
                <br /> Tax ID, SOS ID, Payroll Tax ID, etc.
              </span>
            }
          >
            <span className="t-text-subtext t-text-text-60">
              <ConditionalRender
                condition={Boolean(data?.tax_detail_id)}
                fallback="-"
              >
                {data?.tax_detail_id}
              </ConditionalRender>
            </span>
          </DetailItem>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

export const RegistrationAccordion = ({
  data,
  entityId,
  groupId,
}: {
  data?: RegistrationDetailsResponse;
  entityId: string;
  groupId: string;
}) => {
  return (
    <Accordion.Root
      type="multiple"
      defaultValue={["ABOUT", "DETAILS"]}
      className="t-w-full t-border-0 t-rounded-lg"
    >
      <div className="t-flex t-flex-col t-gap-4">
        <RegistrationAboutAccordion
          data={data}
          entityId={entityId}
          groupId={groupId}
        />
        <RegistrationDetails />
      </div>
    </Accordion.Root>
  );
};
